import { ErrorEvent } from '@sentry/react';

import { uaParser } from 'services/Analytics/core';
import {
    MODULE_SCRIPT_ERROR_PATTERNS,
    RESOURCE_TIMING_BUFFER_SIZE,
    SENTRY_AXIOS,
    UNKNOWN_CHUNK_URL,
} from 'services/Sentry/constants';

import { ChunkLoadContext, ChunkLoadResourceEntry, errorType, Extras, IError, Tags } from './interfaces';

const IOS = 'iOS';
const ANDROID = 'Android';
const CHROME = 'Chrome';
const IOS_MIN_VERSION = 14.3;
const ANDROID_MIN_VERSION = 7;
const CHROME_MIN_VERSION = 59;
const FB_BOT_WIDTH_SIZE = 2000;
const FB_BOT_HEIGHT_SIZE = 2000;
const MAX_TAG_VALUE_LENGTH = 200;
const HTTP_ERROR_STATUS = 400;

const objectEntriesPolyfill = <T extends Record<string, unknown>>(obj: T) => {
    if (!Object.entries) {
        const ownProps = Object.keys(obj);
        let i = ownProps.length;
        const resArray = new Array(i);

        while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]];

        return resArray;
    }

    return Object.entries(obj);
};

export const getCustomTags = (error: IError, type: errorType, extras?: Extras): Tags => {
    const customTags: Record<string, string> = {};

    if (type === SENTRY_AXIOS) {
        customTags.API_ERROR_CODE = extras?.status as string;
    }

    return objectEntriesPolyfill(customTags);
};

export const checkIsOldDevices = (event: ErrorEvent) => {
    const UAParser = new uaParser();
    const userAgent = UAParser.setUA(event?.extra?.agent as string);

    const browser = userAgent?.getBrowser();
    const deviceOS = userAgent?.getOS();

    const parsedOSVersion = parseFloat(deviceOS?.version || '');
    const parsedBrowserVersion = parseFloat(browser?.major || '');

    return (
        (deviceOS?.name === IOS && parsedOSVersion < IOS_MIN_VERSION) ||
        (deviceOS?.name === ANDROID && parsedOSVersion < ANDROID_MIN_VERSION) ||
        (browser?.name === CHROME && parsedBrowserVersion < CHROME_MIN_VERSION)
    );
};

export const checkFbBots = (): boolean => {
    const windowWidth = window.screen.width;
    const windowHeight = window.screen.height;

    return windowWidth === FB_BOT_WIDTH_SIZE && windowHeight === FB_BOT_HEIGHT_SIZE;
};

export const isUnhandledModuleScriptError = (event: ErrorEvent): boolean =>
    Boolean(
        event.exception?.values?.some(
            ({ value, mechanism }) =>
                mechanism?.handled === false &&
                !!value &&
                MODULE_SCRIPT_ERROR_PATTERNS.some((pattern) => pattern.test(value))
        )
    );

export const getResourceEntries = (): PerformanceResourceTiming[] => {
    try {
        if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return [];

        return performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    } catch {
        return [];
    }
};

const parseUrl = (url: string): URL | null => {
    try {
        return new URL(url);
    } catch {
        return null;
    }
};

const getPathname = (url: string): string => parseUrl(url)?.pathname ?? url;

// Cross-origin scripts without Timing-Allow-Origin report decodedBodySize 0 and responseStatus 0 by design,
// so only same-origin entries (where the app chunks are served from) can be judged reliably
const isSameOriginJsEntry = (entry: PerformanceResourceTiming): boolean => {
    const url = parseUrl(entry.name);

    return !!url && url.origin === window.location.origin && url.pathname.endsWith('.js');
};

const getResponseStatus = (entry: PerformanceResourceTiming): number | null => {
    const responseStatus = 'responseStatus' in entry ? (entry as { responseStatus?: unknown }).responseStatus : null;

    return typeof responseStatus === 'number' ? responseStatus : null;
};

const isFailedEntry = (entry: PerformanceResourceTiming): boolean => {
    const responseStatus = getResponseStatus(entry);

    // 0 means a network-level failure (offline, aborted), so fall back to the body check like on Safari
    if (responseStatus) return responseStatus >= HTTP_ERROR_STATUS;

    return entry.decodedBodySize === 0;
};

export const getFailedChunkEntries = (entries: PerformanceResourceTiming[]): PerformanceResourceTiming[] =>
    entries.filter((entry) => isSameOriginJsEntry(entry) && isFailedEntry(entry));

export const getFailedChunkUrl = (failedEntries: PerformanceResourceTiming[]): string => {
    if (!failedEntries.length) return UNKNOWN_CHUNK_URL;

    const pathnames = [...new Set(failedEntries.map((entry) => getPathname(entry.name)))];

    return pathnames.join(',').slice(0, MAX_TAG_VALUE_LENGTH);
};

export const getChunkLoadContext = (
    entries: PerformanceResourceTiming[],
    failedEntries: PerformanceResourceTiming[]
): ChunkLoadContext => ({
    online: typeof navigator === 'undefined' ? null : navigator.onLine,
    page_in_background: typeof document === 'undefined' ? null : document.visibilityState === 'hidden',
    page_age_ms: typeof performance === 'undefined' ? null : Math.round(performance.now()),
    resource_entries: failedEntries.map(
        (entry): ChunkLoadResourceEntry => ({
            url: getPathname(entry.name),
            responseStatus: getResponseStatus(entry),
            transferSize: entry.transferSize,
            decodedBodySize: entry.decodedBodySize,
            duration: Math.round(entry.duration),
        })
    ),
    resource_buffer_full: entries.length >= RESOURCE_TIMING_BUFFER_SIZE,
});
